import Masonry from '@mui/lab/Masonry';
import { useTranslation } from 'react-i18next';
import './InstanceOverview.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Chart as ChartJS } from 'chart.js';
import Debug from 'debug';
import { SyntheticEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Grid, IconButton, Snackbar } from '@mui/material';
import axios from 'axios';
import i18next from 'i18next';
import { Refresh } from '@mui/icons-material';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import ChartPie from '../../../components/charts/ChartPie';
import { register } from '../../../components/charts/charts-data-config';
import { Worker } from '../../../models/Worker';
import { Alarm } from '../../../models/Alarm';
import { Licence } from '../../../models/Licence';

const debug = Debug('monitoring:instance-overview');

function InstanceOverview() {
  ChartJS.register(register);

  const { t } = useTranslation();
  const i18nLang = i18next.language;
  const paramsRoute = useParams();
  const myLocation = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertServerity, setAlertSeverity] = useState(AlertSeverity.ERROR);
  const [meetingCount, setMeetingCount] = useState<number>(0);
  const [participantCount, setParticipantCount] = useState<number>(0);
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [licences, setLicences] = useState<Licence[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [workersLocation, setWorkersLocation] = useState<string[]>([]);
  const [overviewStats, setOverviewStats] = useState<any>();
  const [videoQualityChartData, setVideoQualityChartData] = useState<any>({});
  const [protocolsChartData, setProtocolsChartData] = useState<any>({});

  const getOverviewStats = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/liveStats`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        setMeetingCount(response.data.data.meetingCount);
        setParticipantCount(response.data.data.participantCount);
        setOverviewStats(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          setAlertMessage(err.data.message);
          setAlertSeverity(AlertSeverity.ERROR);
          setOpen(true);
        }
        console.error(error);
      });
  };

  const setCharts = () => {
    if (overviewStats) {
      const protocolsData = overviewStats.protocol;
      const labelsProtocol = Object.keys(protocolsData).sort();
      const dataProtocol = [];
      for (const key of labelsProtocol) {
        if (protocolsData[key]) {
          dataProtocol.push(protocolsData[key]);
        }
      }
      for (const i in labelsProtocol) {
        if (labelsProtocol[i]) {
          labelsProtocol[i] = `${labelsProtocol[i]} (${dataProtocol[i]})`;
        }
      }
      setProtocolsChartData({
        displaySubtitle: false,
        displayTitle: true,
        title: t('InstanceOverview.chartTitle.protocols'),
        data: dataProtocol,
        labels: labelsProtocol,
      });
      const qualityData = overviewStats.quality;
      const labelsQuality = Object.keys(qualityData).sort();
      const dataQuality = [];
      for (const key of labelsQuality) {
        if (qualityData[key]) {
          dataQuality.push(qualityData[key]);
        }
      }
      for (const i in labelsQuality) {
        if (labelsQuality[i]) {
          labelsQuality[i] = `${labelsQuality[i]} (${dataQuality[i]})`;
        }
      }
      setVideoQualityChartData({
        displaySubtitle: true,
        displayTitle: true,
        subtitle: t('InstanceOverview.chartSubtitle.videoQuality', { overviewStats }),
        title: t('InstanceOverview.chartTitle.videoQuality'),
        data: dataQuality,
        labels: labelsQuality,
        quality: true
      });
    }
  };

  const getOverviewAlarms = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/alarms`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setAlarms(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          setAlertMessage(err.data.message);
          setAlertSeverity(AlertSeverity.ERROR);
          setOpen(true);
        }
        console.error(error);
      });
  };

  const getOverviewLicences = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/licences`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setLicences(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          setAlertMessage(err.data.message);
          setAlertSeverity(AlertSeverity.ERROR);
          setOpen(true);
        }
        console.error(error);
      });
  };

  const getOverviewWorkers = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/workers`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setWorkers(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          setAlertMessage(err.data.message);
          setAlertSeverity(AlertSeverity.ERROR);
          setOpen(true);
        }
        console.error(error);
      });
  };

  const organizeWorkers = () => {
    debug(workers);
    const locations: string[] = [];
    workers.forEach((value: any, index: number) => {
      if (!locations.includes(value.systemLocation)) {
        locations.push(value.systemLocation);
      }
    });
    debug(locations);
    setWorkersLocation(locations);
  };

  const getData = () => {
    getOverviewStats();
    getOverviewAlarms();
    getOverviewLicences();
    getOverviewWorkers();
  };
  
  useEffect(() => {
    const pollingInterval = setInterval(() => {
      getData();
    }, 10000);
    getData();
    return () => {
      clearInterval(pollingInterval);
    };
  }, [myLocation]);
  
  useEffect(() => {
    setCharts();
  }, [overviewStats, i18nLang]);

  useEffect(() => {
    organizeWorkers();
  }, [workers]);

  useEffect(() => {
    if (workers?.length > 0 && alarms?.length > 0) {
      for (const worker of workers) {
        worker.alarms = [];
        for (const alarm of alarms) {
          if (alarm.node === worker.address) {
            worker.alarms.push(alarm);
          }
        }
      }
    }
  }, [workers, alarms]);

  const navigateToRealTimeMeetings = () => {
    navigate(`/instance/${paramsRoute.instanceId}/real-time`);
  };

  const navigateToAlarms = () => {
    navigate(`/instance/${paramsRoute.instanceId}/alarms`);
  };

  const navigateToRealTimeStats = () => {
    navigate(`/instance/${paramsRoute.instanceId}/real-time-statistics`);
  };

  const navigateToNode = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        if (response.data.data) {
          window.open(response.data.data.pexipServerUrl);
        }
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
  };

  const navigateToWorker = (workerId: string) => {
    navigate(`/instance/${paramsRoute.instanceId}/configuration/${workerId}`);
  };

  const handleClose = (event?: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const calc80 = (current: number, total: number) => {
    if (((current * 100) / total) >= 80) {
      return true;
    } 
    return false;
  };

  const getClassNamesWorker = (worker: Worker) => {
    let classes = 'pie-load animate pointer';
    if (worker?.alarms?.length > 0) {
      if (worker.alarms.filter((a: Alarm) => a.level === 'error').length > 0) {
        classes += ' is-alarm-error';
      } else {
        classes += ' is-alarm-warning';
      }
    }
    return classes;
  };

  const isEmptyObject = (obj: any) => Object.keys(obj).length === 0; 

  return (
    <>
      <div className="instance-overview">
        <div className="category-container">
          <Button variant="text" onClick={navigateToNode}>{t('InstanceOverview.managementNode')}</Button>
          <Box sx={{ float: 'right', display: 'inline-flex', alignItems: 'center' }}>
            <div>{ t('InstanceOverview.refresh') }: {`${new Date().toLocaleTimeString()}`}</div>
            <IconButton onClick={getData}>
              <Refresh />
            </IconButton>
          </Box>
        </div>
        <Grid container columns={4} alignItems="center" justifyContent="center">
          <div style={{ margin: 8 }} className="category-container">
            <Card className="m-h-250">
              <div className="pointer" onClick={navigateToRealTimeMeetings} aria-hidden="true">
                <div><strong>{ t('InstanceOverview.participantCount') }:&nbsp;</strong>{participantCount}</div>
                <div><strong>{ t('InstanceOverview.meetingCount') }:&nbsp;</strong>{meetingCount}</div>
              </div>
              <hr />
              <div className="pointer" aria-hidden="true">
                {licences.map((licence: any, i: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i}>
                    <div><strong>{ t('InstanceOverview.audioCount') }:&nbsp;</strong><span className={calc80(licence.audioCount, licence.audioTotal) ? 'reach-limits' : ''}>{licence.audioCount} / {licence.audioTotal}</span></div>
                    <div><strong>{ t('InstanceOverview.portCount') }:&nbsp;</strong><span className={calc80(licence.portCount, licence.portTotal) ? 'reach-limits' : ''}>{licence.portCount} / {licence.portTotal}</span></div>
                    <div><strong>{ t('InstanceOverview.vmrCount') }:&nbsp;</strong><span className={calc80(licence.vmrCount, licence.vmrTotal) ? 'reach-limits' : ''}>{licence.vmrCount} / {licence.vmrTotal}</span></div>
                    <hr />
                    <div><strong>{ t('InstanceOverview.ghmCount') }:&nbsp;</strong>{licence.ghmCount} / {licence.ghmTotal}</div>
                    <div><strong>{ t('InstanceOverview.otjCount') }:&nbsp;</strong>{licence.otjCount} / {licence.otjTotal}</div>
                    <div><strong>{ t('InstanceOverview.schedulingCount') }:&nbsp;</strong>{licence.schedulingCount} / {licence.schedulingTotal}</div>
                    <div><strong>{ t('InstanceOverview.systemCount') }:&nbsp;</strong>{licence.systemCount} / {licence.systemTotal}</div>
                    <div><strong>{ t('InstanceOverview.teamsCount') }:&nbsp;</strong>{licence.teamsCount} / {licence.teamsTotal}</div>
                    <div><strong>{ t('InstanceOverview.telehealthCount') }:&nbsp;</strong>{licence.telehealthCount} / {licence.telehealthTotal}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div style={{ margin: 8 }} className="category-container pointer" onClick={navigateToRealTimeStats} aria-hidden="true">
            {(videoQualityChartData?.data?.length > 0 && videoQualityChartData?.labels?.length > 0) ? (
              <ChartPie chartData={videoQualityChartData} legendPlacement="right" cardWidth="400" cardHeight="250" chartHeight="250" />
            ) : <Card><small>{t('InstanceOverview.chartTitle.noDataVideoQuality')}</small></Card>}
          </div>
          <div style={{ margin: 8 }} className="category-container pointer" onClick={navigateToRealTimeStats} aria-hidden="true">
            {(protocolsChartData?.data?.length > 0 && protocolsChartData?.labels?.length > 0) ? (
              <ChartPie chartData={protocolsChartData} legendPlacement="right" cardWidth="400" cardHeight="250" chartHeight="250" />
            ) : <Card><small>{t('InstanceOverview.chartTitle.noDataProtocols')}</small></Card>}
          </div>
          <div style={{ margin: 8 }} className="category-container">
            <Card className="pointer h-250" onClick={navigateToAlarms}>
              <div className="title-worker-load"><strong>{ t('InstanceOverview.alarms') }</strong></div>
              {alarms.map((alarm: any) => (
                <div key={alarm._id} className={`alarm-${alarm.level}`}>{alarm.name}</div>
              ))}
            </Card>
          </div>
        </Grid>
        <Masonry columns={8} spacing={0}>
          {workersLocation.map((location: string) => (
            <div className="category-container" key={location} aria-hidden="true">
              <Card sx={{ margin: 1 }}>
                <div className="title-worker-load"><strong>{location}</strong></div>
                <div className="nodes-type"><strong><small>{ t('InstanceOverview.ConferencingNodes') }</small></strong></div>
                <Grid container alignItems="center" justifyContent="center">
                  {workers.map((worker: any) => (
                    worker.systemLocation === location && worker.nodeType === 'CONFERENCING' && (
                      <div key={worker.id} className={worker?.alarms?.length > 0 ? 'worker-load-elements breath' : 'worker-load-elements'}>
                        <div aria-hidden="true" onClick={() => navigateToWorker(worker.id)} className={getClassNamesWorker(worker)} style={{ ['--p' as any]: worker.mediaLoad, ['--c' as any]: '#BD3156' }}>{worker.mediaLoad}%</div>
                      </div>
                    )
                  ))}
                </Grid>
                <div className="nodes-type"><strong><small>{ t('InstanceOverview.ProxyingNodes') }</small></strong></div>
                <Grid container alignItems="center" justifyContent="center">
                  {workers.map((worker: any) => (
                    worker.systemLocation === location && worker.nodeType === 'PROXYING' && (
                      <div key={worker.id} className={worker?.alarms?.length > 0 ? 'worker-load-elements breath' : 'worker-load-elements'}>
                        <div aria-hidden="true" onClick={() => navigateToWorker(worker.id)} className={getClassNamesWorker(worker)} style={{ ['--p' as any]: worker.mediaLoad, ['--c' as any]: '#BD3156' }}>{worker.mediaLoad}%</div>
                      </div>
                    )
                  ))}
                </Grid>
              </Card>
            </div>
          ))}
        </Masonry>
      </div>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={alertServerity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default InstanceOverview;
